import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { useGeneral } from '../../../contexts';
import { useEventDetails } from '../../../hooks/useEventDetails';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import ImageUploadModal from '../../../components/ui/ImageUploadModal/ImageUploadModal';
import FormInput from '../../../components/ui/Form/FormInput';
import formStyles from '../../../components/ui/Form/Form.module.css';
import { Speaker } from '../../../types/speaker.types';
import { CoverFile } from '../../../types';
import { OrganizerPanelOutletContext, SpeakerFormData } from '../../../types/organizerPanel.types';
import { convertFileToBase64 } from '../../../utils/fileConversion';
import { extractEventIdFromAdminUrl } from '../../../utils/urlSlug';
import styles from './EventSpeakersPage.module.css';

const EventSpeakersPage: React.FC = () => {
  const { channel, event: basicEvent } = useOutletContext<OrganizerPanelOutletContext>();
  const location = useLocation();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  // Extract eventID from URL as fallback when basicEvent isn't available yet
  const eventID = useMemo(() => {
    if (basicEvent?.eventID) return basicEvent.eventID;
    return extractEventIdFromAdminUrl(location.pathname) || '';
  }, [basicEvent?.eventID, location.pathname]);

  // Fetch full event details
  const { event: eventDetails, loading: eventLoading, refreshEvent } = useEventDetails({
    token: token || null,
    eventID,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [speakerImage, setSpeakerImage] = useState<string | null>(null);
  const [speakerImageFile, setSpeakerImageFile] = useState<CoverFile | null>(null);

  // Track initial form data when editing starts
  const initialFormDataRef = useRef<SpeakerFormData | null>(null);
  const initialImageRef = useRef<string | null>(null);
  const initialImageFileRef = useRef<CoverFile | null>(null);

  // Form state
  const [formData, setFormData] = useState<SpeakerFormData>({
    name: '',
    lastname: '',
    about: '',
    speakingAboutTitle: '',
    speakingAbout: '',
    linkedin: '',
    instagram: '',
    x: '',
    website: '',
    email: '',
  });

  // Sort speakers alphabetically by full name
  const speakers = useMemo(() => {
    const speakersList = eventDetails?.speakers || [];
    return [...speakersList].sort((a, b) => {
      const nameA = `${a.name || ''} ${a.lastname || ''}`.trim().toLowerCase();
      const nameB = `${b.name || ''} ${b.lastname || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [eventDetails?.speakers]);

  // Reset form when opening add modal
  useEffect(() => {
    if (showAddModal && !selectedSpeaker) {
      setFormData({
        name: '',
        lastname: '',
        about: '',
        speakingAboutTitle: '',
        speakingAbout: '',
        linkedin: '',
        instagram: '',
        x: '',
        website: '',
        email: '',
      });
      setSpeakerImage(null);
      setSpeakerImageFile(null);
    }
  }, [showAddModal, selectedSpeaker]);

  // Load speaker data when opening edit modal
  useEffect(() => {
    if (showEditModal && selectedSpeaker) {
      const initialFormData: SpeakerFormData = {
        name: selectedSpeaker.name || '',
        lastname: selectedSpeaker.lastname || '',
        about: selectedSpeaker.about || '',
        speakingAboutTitle: selectedSpeaker.speakingAboutTitle || '',
        speakingAbout: selectedSpeaker.speakingAbout || '',
        linkedin: selectedSpeaker.socialMediaLinks?.linkedin || selectedSpeaker.linkedin || '',
        instagram: selectedSpeaker.socialMediaLinks?.instagram || '',
        x: selectedSpeaker.socialMediaLinks?.x || '',
        website: '',
        email: '',
      };
      const initialImage = selectedSpeaker.image || null;
      
      setFormData(initialFormData);
      setSpeakerImage(initialImage);
      setSpeakerImageFile(null);
      
      // Store initial values for comparison
      initialFormDataRef.current = initialFormData;
      initialImageRef.current = initialImage;
      initialImageFileRef.current = null;
    }
  }, [showEditModal, selectedSpeaker]);

  // Check if there are unsaved changes (only for edit mode)
  const hasUnsavedChanges = useMemo(() => {
    if (!showEditModal || !selectedSpeaker || !initialFormDataRef.current) {
      return false;
    }
    
    const initial = initialFormDataRef.current;
    
    // Compare form fields
    const formChanged = (
      formData.name.trim() !== initial.name.trim() ||
      formData.lastname.trim() !== initial.lastname.trim() ||
      formData.about.trim() !== initial.about.trim() ||
      formData.linkedin.trim() !== initial.linkedin.trim()
    );
    
    // Compare image (check if new file was uploaded or image was removed)
    const imageChanged = speakerImageFile !== null || 
      (speakerImage !== initialImageRef.current);
    
    return formChanged || imageChanged;
  }, [showEditModal, selectedSpeaker, formData, speakerImage, speakerImageFile]);

  const handleImageUpload = async (file: File) => {
    try {
      const coverFile = await convertFileToBase64(file);
      setSpeakerImageFile(coverFile);
      const preview = URL.createObjectURL(file);
      setSpeakerImage(preview);
    } catch (error) {
      toastError(getTranslation('organizerPanel.imageUploadError') || 'Resim yüklenemedi');
    }
  };

  const handleAddSpeaker = () => {
    setSelectedSpeaker(null);
    setShowAddModal(true);
  };

  const handleEditSpeaker = (speaker: Speaker) => {
    setSelectedSpeaker(speaker);
    setShowEditModal(true);
  };

  const handleDeleteSpeaker = (speaker: Speaker) => {
    setSelectedSpeaker(speaker);
    setShowDeleteModal(true);
  };

  const handleSaveSpeaker = async () => {
    if (!token || !channel?.channelID || !eventDetails?.eventID) return;

    // Validation
    if (!formData.name.trim()) {
      toastError(getTranslation('organizerPanel.nameRequired') || 'Ad gereklidir');
      return;
    }
    if (!formData.lastname.trim()) {
      toastError(getTranslation('organizerPanel.lastnameRequired') || 'Soyad gereklidir');
      return;
    }
    if (!formData.about.trim()) {
      toastError(getTranslation('organizerPanel.aboutRequired') || 'Hakkında bilgisi gereklidir');
      return;
    }

    setSaving(true);
    try {
      // Send image as CoverFile object (same as event cover) or existing image URL
      const imageToSend = speakerImageFile 
        ? speakerImageFile
        : (selectedSpeaker?.image || null);

      if (selectedSpeaker) {
        // Update speaker
        await Services.SocialHiveUpdateSpeaker(
          token,
          channel.channelID,
          eventDetails.eventID,
          selectedSpeaker.speakerID,
          formData.name.trim(),
          formData.lastname.trim(),
          "-", // speakingAboutTitle - mock value
          "-", // speakingAbout - mock value
          formData.about.trim(),
          imageToSend,
          {
            linkedin: formData.linkedin.trim() || undefined,
            instagram: "string", // mock value
            x: "string", // mock value
          },
          undefined, // sessionStartDateTime
          undefined  // sessionEndDateTime
        );
        toastSuccess(getTranslation('organizerPanel.speakerUpdated') || 'Konuşmacı güncellendi');
      } else {
        // Add speaker
        await Services.SocialHiveAddSpeaker(
          token,
          channel.channelID,
          eventDetails.eventID,
          formData.name.trim(),
          formData.lastname.trim(),
          "-", // speakingAboutTitle - mock value
          "-", // speakingAbout - mock value
          formData.about.trim(),
          imageToSend,
          formData.linkedin.trim() || null
        );
        toastSuccess(getTranslation('organizerPanel.speakerAdded') || 'Konuşmacı eklendi');
      }

      // Refresh event to get updated speakers list
      refreshEvent();
      
      // Close modal and reset
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedSpeaker(null);
      setSpeakerImage(null);
      setSpeakerImageFile(null);
      initialFormDataRef.current = null;
      initialImageRef.current = null;
      initialImageFileRef.current = null;
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        (selectedSpeaker 
          ? getTranslation('organizerPanel.speakerUpdateError') || 'Konuşmacı güncellenemedi'
          : getTranslation('organizerPanel.speakerAddError') || 'Konuşmacı eklenemedi');
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    if (showEditModal && hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedSpeaker(null);
      initialFormDataRef.current = null;
      initialImageRef.current = null;
      initialImageFileRef.current = null;
    }
  };

  const handleUnsavedModalClose = () => {
    setShowUnsavedModal(false);
  };

  const handleUnsavedModalDiscard = () => {
    // Reset form to initial values
    if (initialFormDataRef.current) {
      setFormData(initialFormDataRef.current);
      setSpeakerImage(initialImageRef.current);
      setSpeakerImageFile(initialImageFileRef.current);
    }
    
    setShowUnsavedModal(false);
    setShowEditModal(false);
    setSelectedSpeaker(null);
    initialFormDataRef.current = null;
    initialImageRef.current = null;
    initialImageFileRef.current = null;
  };

  const handleUnsavedModalSave = async () => {
    setShowUnsavedModal(false);
    await handleSaveSpeaker();
  };

  const handleConfirmDelete = async () => {
    if (!token || !selectedSpeaker?.speakerID || !eventDetails?.eventID) return;

    setDeleting(true);
    try {
      await Services.SocialHiveRemoveSpeaker(token, eventDetails.eventID, selectedSpeaker.speakerID);
      toastSuccess(getTranslation('organizerPanel.speakerDeleted') || 'Konuşmacı silindi');
      
      // Refresh event to get updated speakers list
      refreshEvent();
      
      // Close modal
      setShowDeleteModal(false);
      setSelectedSpeaker(null);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.speakerDeleteError') || 'Konuşmacı silinemedi';
      toastError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={styles.eventSpeakersPage}>
      {(eventLoading || !eventDetails) && (
        <>
          <div className={styles.header}>
            <LoadingSkeleton height={32} width="200px" />
            <LoadingSkeleton height={40} width="120px" />
          </div>
          <div className={styles.content}>
            <div className={styles.speakersList}>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`skeleton-${index}`} className={styles.speakerCard}>
                  <LoadingSkeleton height={80} width={80} variant="circular" />
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '8px' }}>
                      <LoadingSkeleton height={20} width="60%" />
                    </div>
                    <LoadingSkeleton height={16} width="80%" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {!eventLoading && eventDetails && (
        <>
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onUpload={handleImageUpload}
        title={getTranslation('organizerPanel.uploadSpeakerImage') || 'Konuşmacı Fotoğrafı Yükle'}
        aspectRatio="1:1"
        recommendedSize="400x400"
        getTranslation={getTranslation}
      />

      <Modal
        isOpen={showAddModal || showEditModal}
        onClose={showEditModal ? handleCloseEditModal : () => {
          setShowAddModal(false);
          setSelectedSpeaker(null);
        }}
        title={selectedSpeaker 
          ? (getTranslation('organizerPanel.editSpeaker') || 'Konuşmacı Düzenle')
          : (getTranslation('organizerPanel.addSpeaker') || 'Konuşmacı Ekle')
        }
        variant="default"
        className={styles.speakerModal}
      >
        <div className={styles.modalContent}>
          {/* Image Upload */}
          <div className={styles.imageSection}>
            <label className={styles.label}>
              {getTranslation('organizerPanel.speakerImage') || 'Konuşmacı Fotoğrafı'}
            </label>
            <div
              className={styles.imagePlaceholder}
              onClick={() => setShowImageModal(true)}
            >
              {speakerImage ? (
                <img src={speakerImage} alt="Speaker" className={styles.speakerImageModal} />
              ) : (
                <div className={styles.uploadIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className={formStyles.formFields}>
            <div className={formStyles.formRow}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.firstName') || 'Ad'} *
                </label>
                <FormInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={getTranslation('organizerPanel.firstNamePlaceholder') || 'Ad'}
                />
              </div>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.lastName') || 'Soyad'} *
                </label>
                <FormInput
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                  placeholder={getTranslation('organizerPanel.lastNamePlaceholder') || 'Soyad'}
                />
              </div>
            </div>

            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.titlePosition') || 'Ünvan/Pozisyon'} *
              </label>
              <FormInput
                type="text"
                value={formData.about}
                onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                placeholder={getTranslation('organizerPanel.titlePositionPlaceholder') || 'Örn: CEO, Profesör'}
              />
            </div>

            <div className={formStyles.formField}>
              <label className={styles.label}>
                {getTranslation('organizerPanel.linkedin') || 'LinkedIn'}
              </label>
              <FormInput
                type="text"
                value={formData.linkedin}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  setFormData({ ...formData, linkedin: numericValue });
                }}
                placeholder={getTranslation('organizerPanel.linkedinPlaceholder') || 'LinkedIn ID'}
              />
            </div>
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              size="medium"
              onClick={showEditModal ? handleCloseEditModal : () => {
                setShowAddModal(false);
                setSelectedSpeaker(null);
              }}
              disabled={saving}
            >
              {getTranslation('common.cancel') || 'İptal'}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSaveSpeaker}
              disabled={saving}
            >
              {saving
                ? (getTranslation('common.saving') || 'Kaydediliyor...')
                : (getTranslation('common.save') || 'Kaydet')
              }
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showUnsavedModal}
        onClose={handleUnsavedModalClose}
        title={getTranslation('organizerPanel.unsavedChanges') || 'Kaydedilmemiş Değişiklikler'}
        variant="confirm"
        message={getTranslation('organizerPanel.unsavedChangesMessage') || 'Kaydedilmemiş değişiklikleriniz var. Devam etmek istediğinizden emin misiniz?'}
        confirmText={getTranslation('organizerPanel.saveAndContinue') || 'Kaydet ve Devam Et'}
        cancelText={getTranslation('organizerPanel.discardChanges') || 'Vazgeç'}
        onConfirm={handleUnsavedModalSave}
        onCancel={handleUnsavedModalDiscard}
        confirmVariant="primary"
      />

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSpeaker(null);
        }}
        title={getTranslation('organizerPanel.deleteSpeaker') || 'Konuşmacı Sil'}
        variant="confirm"
        message={getTranslation('organizerPanel.deleteSpeakerConfirm') || 'Bu konuşmacıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.'}
        confirmText={getTranslation('common.delete') || 'Sil'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleConfirmDelete}
        confirmVariant="primary"
        isDanger={true}
      />

      <div className={styles.header}>
        <h2 className={styles.pageTitle}>
          {getTranslation('organizerPanel.eventSpeakers') || 'Konuşmacılar'}
        </h2>
        <Button
          variant="primary"
          size="medium"
          onClick={handleAddSpeaker}
          className={styles.addButton}
        >
          {getTranslation('organizerPanel.addSpeaker') || 'Konuşmacı Ekle'}
        </Button>
      </div>

      <div className={styles.content}>
        {speakers.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyMessage}>
              {getTranslation('organizerPanel.noSpeakers') || 'Henüz konuşmacı eklenmemiş'}
            </p>
          </div>
        ) : (
          <div className={styles.speakersGrid}>
            {speakers.map((speaker) => {
              const fullName = `${speaker.name} ${speaker.lastname}`;
              const avatarUrl = speaker.image || null;

              return (
                <div key={speaker.speakerID} className={styles.speakerCard}>
                  <div className={styles.speakerActions}>
                    <button
                      onClick={() => handleEditSpeaker(speaker)}
                      className={styles.actionIconButton}
                      aria-label={getTranslation('common.edit') || 'Düzenle'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteSpeaker(speaker)}
                      className={styles.deleteIconButton}
                      aria-label={getTranslation('common.delete') || 'Sil'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  <div className={styles.speakerImage}>
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={fullName} />
                    ) : (
                      <div className={styles.speakerInitials}>
                        {speaker.name?.[0]?.toUpperCase() || ''}{speaker.lastname?.[0]?.toUpperCase() || ''}
                      </div>
                    )}
                  </div>
                  <div className={styles.speakerInfo}>
                    <h3 className={styles.speakerName}>{fullName}</h3>
                    {speaker.about && (
                      <p className={styles.speakerTitle}>{speaker.about}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default EventSpeakersPage;
